import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK, humanReadableBytes } from '@flakiness/shared/common/utils.js';
import { Task } from '@lit/task';
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Temporal } from 'temporal-polyfill';
import { linkStyles } from './cssstyles.js';
import { FKUsageRangeEvent, UsageChartRange } from './usage-chart.js';

type ChartId = 'reportBytes' | 'attachmentBytes' | 'runsCount' | 'testsCount';

type ChartConfig = {
  label: string,
  unit: string,
  value: (metric: WireTypes.DayMetrics) => number,
  // Names what was uploaded over the charted window, e.g. "Uploaded 4.2 GB of attachments".
  describe: (total: number) => string,
};

const CHART_CONFIGS: Record<ChartId, ChartConfig> = {
  reportBytes: {
    label: 'Report storage',
    unit: 'bytes',
    value: metric => metric.reportBytes,
    describe: total => `Uploaded ${humanReadableBytes(total)} of reports`,
  },
  attachmentBytes: {
    label: 'Attachment storage',
    unit: 'bytes',
    value: metric => metric.attachmentBytes,
    describe: total => `Uploaded ${humanReadableBytes(total)} of attachments`,
  },
  runsCount: {
    label: 'Reports',
    unit: 'reports',
    value: metric => metric.runsCount,
    describe: total => `Uploaded ${total.toLocaleString()} reports`,
  },
  testsCount: {
    label: 'Test results',
    unit: 'test results',
    value: metric => metric.testsCount,
    describe: total => `Uploaded ${total.toLocaleString()} test results`,
  },
};

const CHART_ORDER: ChartId[] = ['reportBytes', 'attachmentBytes', 'runsCount', 'testsCount'];

function formatDate(date: Temporal.PlainDate): string {
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatMonthDay(date: Temporal.PlainDate): string {
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

@customElement('usage-metrics')
export class UsageMetrics extends LitElement {

  @property({ attribute: false }) metrics?: WireTypes.DayMetrics[];
  @property({ attribute: false }) timezone?: string;
  @property({ attribute: false }) days: number = 180;
  // An explicit range, for callers that chart everything they have instead of a
  // trailing window. Takes precedence over `timezone` / `days` when both are set.
  @property({ attribute: false }) since?: Temporal.PlainDate;
  @property({ attribute: false }) until?: Temporal.PlainDate;

  // What each chart currently plots. Charts brush independently, so a chart
  // that has not reported yet simply falls back to the whole period.
  @state() private _ranges: Partial<Record<ChartId, UsageChartRange>> = {};

  private _rangeTask = new Task(this, {
    args: () => [this.timezone, this.days, this.since, this.until] as const,
    task: async([timezone, days, since, until]) => {
      if (since && until)
        return { since, until };
      CHECK(timezone);
      const now = Temporal.Now.plainDateISO(timezone);
      return {
        since: now.subtract({ days }),
        until: now,
      }
    }
  })

  private _usageMetrics = new Task(this, {
    args: () => [this.metrics, this._rangeTask.value] as const,
    task: async ([metrics, range], { signal }) => {
      CHECK(metrics && range);
      return metrics.filter(metric => {
        const d = Temporal.PlainDate.from(metric.day);
        return Temporal.PlainDate.compare(range.since, d) <= 0 && Temporal.PlainDate.compare(d, range.until) <= 0;
      });
    },
  });

  // Charts take a plain { day, value } series, and re-mapping it on every render
  // would hand each chart a brand new array and force a full redraw.
  private _seriesCache = new Map<ChartId, { source: WireTypes.DayMetrics[], series: { day: string, value: number }[] }>();

  private _series(chartId: ChartId, metrics: WireTypes.DayMetrics[]): { day: string, value: number }[] {
    const cached = this._seriesCache.get(chartId);
    if (cached?.source === metrics)
      return cached.series;
    const series = metrics.map(metric => ({ day: metric.day, value: CHART_CONFIGS[chartId].value(metric) }));
    this._seriesCache.set(chartId, { source: metrics, series });
    return series;
  }

  // Names the charted range in the chart headers. Kept terse: the range changes
  // with every scrub, and a header long enough to wrap would take a line back
  // off the chart on every drag.
  private _period(range?: UsageChartRange): string {
    if (range && !range.isFullPeriod) {
      return range.since.year === range.until.year
        ? `${formatMonthDay(range.since)} – ${formatDate(range.until)}`
        : `${formatDate(range.since)} – ${formatDate(range.until)}`;
    }
    if (this.since && this.until)
      return `since ${formatDate(this.since)}`;
    return `last ${this.days} days`;
  }

  private _renderChart(chartId: ChartId) {
    const config = CHART_CONFIGS[chartId];
    const metrics = this._usageMetrics.value ?? [];
    const range = this._ranges[chartId];
    const charted = range ? metrics.filter(metric => {
      const day = Temporal.PlainDate.from(metric.day);
      return Temporal.PlainDate.compare(range.since, day) <= 0 && Temporal.PlainDate.compare(day, range.until) <= 0;
    }) : metrics;
    const total = charted.reduce((acc, metric) => acc + config.value(metric), 0);
    // The header is clamped to a single line, so it carries its own tooltip for
    // the narrow windows where it gets cut short.
    const label = `${config.describe(total)} · ${this._period(range)}`;
    return html`
      <usage-chart
        .unit=${config.unit}
        .since=${this._rangeTask.value?.since}
        .until=${this._rangeTask.value?.until}
        .metrics=${this._series(chartId, metrics)}
        @fk-usage-range=${(event: FKUsageRangeEvent) => this._ranges = { ...this._ranges, [chartId]: event.range }}
      ><span title=${label}>${label}</span></usage-chart>
    `;
  }

  override render() {
    return html`
      <fk-tabbar>
        ${CHART_ORDER.map(chartId => html`
          <fk-tab name=${chartId}>${CHART_CONFIGS[chartId].label}</fk-tab>
        `)}
        ${CHART_ORDER.map(chartId => html`
          <fk-tabpane name=${chartId}>${this._renderChart(chartId)}</fk-tabpane>
        `)}
      </fk-tabbar>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: flex;
      flex-direction: column;
      flex: auto;
      min-width: 0;
    }

    usage-chart {
      height: 270px;
    }
  `];
}
